import type { ApiResponse } from "@/features/auth/services/authApi";
import { apiClient } from "@/shared/services/api";

export interface CallLog {
  id: string;
  callerName: string;
  phone?: string;
  email?: string;
  subject: string;
  inquiryDetails: string;
  status: number;
  statusName: string;
  priority: number;
  priorityName: string;
  inquiryDate: string;
  direction: number;
  directionName: string;
  source: number;
  sourceName: string;
  productServiceInterest?: string;
  followUpDate?: string;
  remarks?: string;
  resolutionNotes?: string;
  assignedToUserId?: string;
  assignedToUserName?: string;
  customerId?: string;
  customerName?: string;
  createdAt: string;
  updatedAt?: string;
  createdByUserId?: string;
}

export interface CreateCallLogRequest {
  callerName: string;
  phone?: string;
  email?: string;
  subject: string;
  inquiryDetails: string;
  priority: number;
  inquiryDate: string;
  direction: number;
  source: number;
  productServiceInterest?: string;
  followUpDate?: string;
  remarks?: string;
  resolutionNotes?: string;
  assignedToUserId?: string;
  customerId?: string;
}

export interface UpdateCallLogRequest extends CreateCallLogRequest {
  status: number;
}

export interface CallLogsListQuery {
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
  status?: number;
  dueFollowUpsOnly?: boolean;
}

export interface CallLogsListResult {
  items: CallLog[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export const callLogsApi = {
  getAllCallLogs: async (query: CallLogsListQuery = {}): Promise<CallLogsListResult> => {
    const { data } = await apiClient.get<ApiResponse<CallLogsListResult>>("/CallLogs", {
      params: {
        pageNumber: query.pageNumber,
        pageSize: query.pageSize,
        searchTerm: query.searchTerm,
        status: query.status,
        dueFollowUpsOnly: query.dueFollowUpsOnly,
      },
    });
    return data.data;
  },

  getCallLogById: async (id: string): Promise<CallLog> => {
    const { data } = await apiClient.get<ApiResponse<CallLog>>(`/CallLogs/${id}`);
    return data.data;
  },

  createCallLog: async (payload: CreateCallLogRequest): Promise<CallLog> => {
    const { data } = await apiClient.post<ApiResponse<CallLog>>("/CallLogs", payload);
    return data.data;
  },

  updateCallLog: async (id: string, payload: UpdateCallLogRequest): Promise<CallLog> => {
    const { data } = await apiClient.put<ApiResponse<CallLog>>(`/CallLogs/${id}`, payload);
    return data.data;
  },

  deleteCallLog: async (id: string): Promise<void> => {
    await apiClient.delete(`/CallLogs/${id}`);
  },

  getCallLogsByStatus: async (status: number): Promise<CallLog[]> => {
    const { data } = await apiClient.get<ApiResponse<CallLog[]>>(`/CallLogs/status/${status}`);
    return data.data;
  },

  getDueFollowUps: async (): Promise<CallLog[]> => {
    const { data } = await apiClient.get<ApiResponse<CallLog[]>>("/CallLogs/due-follow-ups");
    return data.data;
  },

  searchCallLogs: async (searchTerm: string): Promise<CallLog[]> => {
    const { data } = await apiClient.get<ApiResponse<CallLog[]>>("/CallLogs/search", {
      params: { searchTerm },
    });
    return data.data;
  },
};
